/**
 * Toast payment error classifier — TEAM-12423
 *
 * Maps Toast SDK error messages from confirm_payment / create_payment_method
 * terminal states into recovery categories, each routed to a distinct
 * diner-facing experience (modal, banner, or redirect).
 */

import { PAYMENT_ERROR_COPY } from '@/constants/paymentErrorCopy';
import { type EventErrorContent } from '@/types/toast';
import { NETWORK_ERROR_MESSAGES } from '@/utils/networkErrors';

// ---------------------------------------------------------------------------
// TEAM-12423: Recovery-category classifier (modal / banner routing)
// ---------------------------------------------------------------------------

/**
 * The error shape accepted by the classifier. The Toast SDK sends errors in
 * several nested shapes (cause.message, developerMessage, message), and
 * confirm_payment can also throw plain strings.
 */
type ClassifiableError = EventErrorContent | string | null | Record<string, unknown>;

export enum ToastRecoveryCategory {
  /** Card declined or generic payment processing error — show retry modal */
  RE_ENTER = 'RE_ENTER',
  /** Transient server-side issue — auto-retry once, then show retry modal */
  TRANSIENT_RETRY = 'TRANSIENT_RETRY',
  /** Apple Pay / Google Pay failure — inline banner, auto-select card entry */
  WALLET_ESCALATE = 'WALLET_ESCALATE',
  /** Order may have gone through but confirmation failed — show status-unknown modal */
  STATUS_UNKNOWN = 'STATUS_UNKNOWN',
  /** Session or configuration problem (e.g. 401 unauthorized) — show neutral retry modal */
  SESSION_OR_CONFIG = 'SESSION_OR_CONFIG',
}

/**
 * Whether a recovery category is surfaced as the blocking <ToastRecoveryModal /> — every category
 * except WALLET_ESCALATE, whose surface is the inline wallet banner. TEAM-15276: the one
 * definition shared by the modal's mount gate (pages/checkout.tsx) and the places that keep the
 * checkout status bar and the inline card error quiet while it shows, so the modal's message is
 * the only failure message on the page and the two can never drift apart.
 */
export const isToastRecoveryModalCategory = (
  category: ToastRecoveryCategory | null | undefined,
): category is Exclude<ToastRecoveryCategory, ToastRecoveryCategory.WALLET_ESCALATE> =>
  !!category && category !== ToastRecoveryCategory.WALLET_ESCALATE;

interface ClassificationResult {
  category: ToastRecoveryCategory;
  /** The original SDK message substring that matched */
  matchedPattern: string;
}

/**
 * Ordered list of patterns. First match wins.
 * Patterns are matched case-insensitively against the full error message chain
 * (SDK message + developerMessage + cause.message).
 */
const CLASSIFICATION_RULES: {
  pattern: string | RegExp;
  category: ToastRecoveryCategory;
}[] = [
  // STATUS_UNKNOWN — must match before RE_ENTER patterns
  {
    pattern: 'failed to fetch',
    category: ToastRecoveryCategory.STATUS_UNKNOWN,
  },
  {
    pattern: 'payment intent cannot be modified - it may have already been processed',
    category: ToastRecoveryCategory.STATUS_UNKNOWN,
  },
  {
    pattern: 'intent cannot be modified in its current status',
    category: ToastRecoveryCategory.STATUS_UNKNOWN,
  },

  // WALLET_ESCALATE
  {
    pattern: 'unknown error occurred during apple pay',
    category: ToastRecoveryCategory.WALLET_ESCALATE,
  },
  {
    pattern: 'issue finalizing your google pay',
    category: ToastRecoveryCategory.WALLET_ESCALATE,
  },

  // SESSION_OR_CONFIG — Toast rejected the payment session (401 / unauthorized).
  // TEAM-15548: must match before RE_ENTER catch-alls so a 401 is never shown
  // as a card problem. "unauthorized" does NOT substring-match the existing
  // RE_ENTER pattern "payment was not able to be authorized" (no "un" prefix).
  // These rules are skipped when the error also carries a terminal decline
  // signature — see `classifyToastError`.
  {
    pattern: 'unauthorized',
    category: ToastRecoveryCategory.SESSION_OR_CONFIG,
  },
  {
    pattern: 'authentication failed',
    category: ToastRecoveryCategory.SESSION_OR_CONFIG,
  },
  {
    pattern: 'full authentication is required',
    category: ToastRecoveryCategory.SESSION_OR_CONFIG,
  },

  // 'load failed' is the Safari/WebKit equivalent of Chrome's 'failed to fetch' —
  // both indicate a network-level failure where the payment may have been processed
  // server-side. Route to STATUS_UNKNOWN to avoid duplicate charges.
  // Word-boundary match avoids false positives on "download failed", "preload failed", etc.
  {
    pattern: /\bload failed\b/,
    category: ToastRecoveryCategory.STATUS_UNKNOWN,
  },

  // TRANSIENT_RETRY
  {
    pattern: 'please try again later',
    category: ToastRecoveryCategory.TRANSIENT_RETRY,
  },

  // RE_ENTER — catch-all payment processing errors
  {
    pattern: 'error occurred processing your card payment',
    category: ToastRecoveryCategory.RE_ENTER,
  },
  {
    pattern: 'payment was not able to be authorized',
    category: ToastRecoveryCategory.RE_ENTER,
  },
];

/**
 * Build a single searchable string from the Toast SDK error object.
 * The SDK nests messages in different places depending on the error type.
 */
function buildSearchString(error: ClassifiableError): string {
  const parts: string[] = [];
  if (typeof error === 'string') {
    parts.push(error);
  } else if (error) {
    // Probe arbitrary properties — the SDK nests messages differently per error type.
    const obj = error as Record<string, unknown>;
    const cause = obj.cause as Record<string, unknown> | undefined;
    if (typeof obj.message === 'string') parts.push(obj.message);
    if (typeof obj.developerMessage === 'string') parts.push(obj.developerMessage);
    if (cause && typeof cause.message === 'string') parts.push(cause.message);
    if (cause && typeof cause.developerMessage === 'string') parts.push(cause.developerMessage);
  }
  return parts.join(' ').toLowerCase();
}

/**
 * Classify a Toast payment error into a recovery category.
 * Returns null if the error doesn't match any known pattern (will fall through
 * to the existing generic error handling).
 */
export function classifyToastError(error: ClassifiableError): ClassificationResult | null {
  const searchString = buildSearchString(error);
  if (!searchString) return null;

  // TEAM-15548: the SESSION_OR_CONFIG rules are unanchored substrings over every field, so a
  // decline whose cause is "Payment was not able to be authorized." and whose developerMessage
  // happens to say "unauthorized" would match them first. A terminal decline signature anywhere
  // in the error wins: telling a declined diner to retry the same card is the worse mistake.
  const carriesDeclineSignature = TERMINAL_CAUSE_SIGNATURES.some((signature) =>
    searchString.includes(signature),
  );

  for (const rule of CLASSIFICATION_RULES) {
    if (carriesDeclineSignature && rule.category === ToastRecoveryCategory.SESSION_OR_CONFIG) {
      continue;
    }
    const matched =
      rule.pattern instanceof RegExp
        ? rule.pattern.test(searchString)
        : searchString.includes(rule.pattern);
    if (matched) {
      const matchedPattern = rule.pattern instanceof RegExp ? rule.pattern.source : rule.pattern;
      return { category: rule.category, matchedPattern };
    }
  }

  return null;
}

/**
 * Diner-facing copy per recovery category, matching Figma node 22250:33333.
 */
export const TOAST_RECOVERY_COPY = {
  [ToastRecoveryCategory.RE_ENTER]: {
    heading: "We couldn't finish placing your order.",
    body: 'Your cart is saved. Give it another try in a moment.',
    primaryButton: 'Try Again',
    secondaryButton: 'Use another payment method',
  },
  [ToastRecoveryCategory.TRANSIENT_RETRY]: {
    heading: "We couldn't finish placing your order.",
    body: 'Your cart is saved. Give it another try in a moment.',
    primaryButton: 'Try Again',
    secondaryButton: 'Use another payment method',
  },
  [ToastRecoveryCategory.STATUS_UNKNOWN]: {
    heading: 'Your payment may have gone through',
    body: 'We lost connection before confirming your order. Check your order history before trying again to avoid a duplicate charge.',
    primaryButton: 'Find my order',
    secondaryButton: 'Contact restaurant',
    tertiaryButton: 'Try placing the order again',
  },
  [ToastRecoveryCategory.SESSION_OR_CONFIG]: {
    heading: "We couldn't complete your payment right now.",
    body: 'Please try again in a moment or choose another payment method.',
    primaryButton: 'Try Again',
    secondaryButton: 'Use another payment method',
  },
  // WALLET_ESCALATE uses inline banners, not a modal — copy is per-wallet-type
  [ToastRecoveryCategory.WALLET_ESCALATE]: {
    applePay: "Apple Pay isn't available right now. You can pay with a card instead.",
    googlePay: "Google Pay isn't available right now. You can pay with a card instead.",
    cancelledApplePay:
      'Apple Pay cancelled. No payment was submitted. You can try again or choose a different payment method.',
    cancelledGooglePay:
      'Google Pay cancelled. No payment was submitted. You can try again or choose a different payment method.',
  },
} as const;

/**
 * Copy for the multi-failure (repeat payment) modal — shown after 2+
 * consecutive confirm_payment failures on the same basket.
 */
export const TOAST_REPEAT_FAILURE_COPY = {
  heading: 'Still having trouble reaching the restaurant.',
  body: 'Your cart is saved. You can try again, or save it for later and check back in a few minutes.',
  primaryButton: 'Try Again',
  secondaryButton: 'Save Cart for Later',
} as const;

// ---------------------------------------------------------------------------
// TEAM-14895: Decline-class classifier (transient vs terminal copy)
// ---------------------------------------------------------------------------

/**
 * TEAM-14895: whether a Toast `confirmIntent` failure is worth retrying against the same card.
 *
 * Toast's failure contract is only two free-text strings and a boolean (`EventErrorContent`,
 * types/toast.ts) — there is no decline code and no decline reason. The `has_decline_reason`
 * Sentry tag is NOT one: it is a presence check on `cause.message` (toastErrorLogger.ts), which
 * is why it reads `true` on 100% of both COO-WEBAPP-3BW and COO-WEBAPP-3EF. So free-text matching
 * is the only signal available, exactly as `classifySubmitFailure` matches coo-api decline text
 * for the same reason.
 */
export enum ToastDeclineClass {
  /** A retry against the same card can plausibly succeed — no card decision was made, or Toast said to retry. */
  TRANSIENT = 'transient',
  /** Retrying the same card cannot help — the diner must re-enter it or use another method. */
  TERMINAL = 'terminal',
  /**
   * Cause text matching no signature below, or no cause text at all. Both take the terminal copy;
   * the value exists so TEAM-14379 can size that residue from real events rather than guess at it.
   * The two are told apart in Sentry by `has_decline_reason`, which is exactly the cause-text
   * presence check this class does not fold in: `unknown` + `has_decline_reason: true` is wording
   * we do not recognise, `unknown` + `false` is a failure Toast sent us with no text at all.
   */
  UNKNOWN = 'unknown',
}

/**
 * Cause text we have direct evidence is terminal — the two signatures this ticket is named for,
 * taken verbatim from production Sentry (COO-WEBAPP-3BW and COO-WEBAPP-3EF, measured 2026-09-18).
 *
 * 3BW ("An error occurred processing your card payment.") is terminal despite the ticket's framing
 * calling it retryable, because nothing in the data supports the framing: it carries
 * `retryable: true` on 5082/5082 events and recurs on the same basket moments later as 3EF, which
 * is the signature of a card that will not authorize rather than of a blip.
 */
const TERMINAL_CAUSE_SIGNATURES: readonly string[] = [
  'an error occurred processing your card payment',
  'payment was not able to be authorized',
].map((wording) => wording.toLowerCase());

/**
 * Cause text we have direct evidence is transient. Deliberately short.
 *
 * TEAM-14379 — the investigation that would say which Toast failures are genuine declines and
 * which are our own defect — has not started, so this list is evidence-only and everything else
 * falls through to the conservative side (see `classifyToastDecline`). Each entry earns its place:
 *
 * - `please try again later` — Toast itself instructs a retry.
 * - every wording in {@link NETWORK_ERROR_MESSAGES} — transport failures. The confirm never
 *   reached Toast, so no card decision was made and the same card is untested. Sourced from the
 *   shared constant rather than copied: this app ships inside an Android WebView (CLAUDE.md), where
 *   "Network request failed" is a wording we actually see, and a partial copy of that list would
 *   classify a pure transport failure as terminal and tell the diner to change cards.
 */
const TRANSIENT_CAUSE_SIGNATURES: readonly string[] = [
  'please try again later',
  ...NETWORK_ERROR_MESSAGES,
].map((wording) => wording.toLowerCase());

/** Every free-text field Toast populates on a failure, lowercased and trimmed, empties dropped. */
function buildCauseFields(error: EventErrorContent | null | undefined): string[] {
  if (!error?.cause) {
    return [];
  }

  return [error.cause.message, error.cause.developerMessage]
    .filter(Boolean)
    .map((field) => field.toLowerCase().trim())
    .filter(Boolean);
}

/**
 * Whether any single cause field *opens* with one of these signatures.
 *
 * Anchored per field rather than `includes` over the two concatenated, which is the discipline
 * `isNetworkError` already applies to a real `Error` message and exists for the same reason: each
 * of Toast's wordings is the start of a sentence it composes, so the same words appearing later in
 * some other sentence are a fragment, not that signature. A cause like
 * `"Do not honor. Please try again later."` is a decline that happens to quote a retry
 * instruction, and a substring test would read it as transient and send the diner back to a dead
 * card — the exact fail-open direction this classifier's conservative default exists to avoid.
 * Prefix rather than equality because Chrome appends the failed origin to its transport wording
 * (see `isNetworkError`) and Toast's own strings vary in trailing punctuation.
 */
function opensWithSignature(fields: string[], signatures: readonly string[]): boolean {
  return fields.some((field) => signatures.some((signature) => field.startsWith(signature)));
}

/**
 * TEAM-14895 AC2: combine the SDK `retryable` flag with the cause text.
 *
 * `retryable` alone cannot discriminate — it is `true` on 100% of 3BW and 99.4% of 3EF — so it is
 * never a positive signal. It is read only in the one direction that carries information: an
 * explicit `retryable: false` is the SDK stating a verdict, and no wording can earn retry copy
 * over it.
 *
 * The order below is the fail-safe one. A known-terminal signature wins outright, so a decline
 * that quotes a retry instruction stays terminal. Text matching nothing is UNKNOWN rather than
 * TERMINAL: it reads the same to the diner — {@link getToastDeclineCopy} gives both the
 * re-enter-card copy — but it is the honest telemetry, and it is what lets TEAM-14379 size the
 * wording we do not yet recognise. Both beat the alternative for the diner: one wrongly told to
 * re-enter their card loses a step, while one wrongly told to retry burns attempt after attempt on
 * a card that will never authorize, which is the abandoned checkout this ticket exists to stop.
 */
export function classifyToastDecline(
  error: EventErrorContent | null | undefined,
): ToastDeclineClass {
  const causeFields = buildCauseFields(error);

  if (opensWithSignature(causeFields, TERMINAL_CAUSE_SIGNATURES)) {
    return ToastDeclineClass.TERMINAL;
  }

  // The SDK saying `false` is an affirmative verdict, unlike the mere absence of a match below —
  // so it is read BEFORE the no-cause-text exit. A failure that carries no text at all but is
  // explicitly flagged unretryable is the one case where Toast tells us something outright, and
  // discarding it as `unknown` would inflate exactly the residue TEAM-14379 is meant to size.
  if (error?.retryable === false) {
    return ToastDeclineClass.TERMINAL;
  }

  if (causeFields.length === 0) {
    return ToastDeclineClass.UNKNOWN;
  }

  if (opensWithSignature(causeFields, TRANSIENT_CAUSE_SIGNATURES)) {
    return ToastDeclineClass.TRANSIENT;
  }

  return ToastDeclineClass.UNKNOWN;
}

/**
 * Diner-facing copy, TEAM-14895 AC3/AC4.
 *
 * Both strings come from `@/constants/paymentErrorCopy` rather than being declared here, so they
 * inherit TEAM-13884's governed catalog and the guardrail test that pins it
 * (`utils/__tests__/paymentErrorCopy.ts`: no markup, ASCII only, length, terminal punctuation, the
 * coo-api and INITIALIZATION_ERRORS substring collisions, the `isPaymentError`
 * self-misclassification guard, and the charge-state ban). A diner payment sentence declared in a
 * utils file would sit outside every one of those.
 *
 * `cardPaymentFailed` is the string TEAM-13884 already shows on this path, so a terminal decline
 * reads exactly as it does today and only a transient one changes. Neither claims nor denies that
 * the card was charged: a Toast failure after authorization but before capture can leave a hold,
 * so "you have not been charged" could be a lie. Neither carries raw `cause` text, per TEAM-13882.
 */
export function getToastDeclineCopy(declineClass: ToastDeclineClass): string {
  // UNKNOWN deliberately shares the terminal copy — see `ToastDeclineClass.UNKNOWN`. It stays its
  // own value in telemetry, which is the only place the two need to differ.
  return declineClass === ToastDeclineClass.TRANSIENT
    ? PAYMENT_ERROR_COPY.cardPaymentFailedRetryable
    : PAYMENT_ERROR_COPY.cardPaymentFailed;
}
